import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, Zap, Shield, Trophy } from "lucide-react";
import { SERVICES } from "@/data/services";

const TESTIMONIALS = [
  { name: "Sarah Chen", role: "Founder, Northwind Labs", quote: "TelstraNics rebuilt our marketing site in 6 weeks. Traffic up 3x, sign-ups up 5x. Best partner we've had.", avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop" },
  { name: "Marcus Reid", role: "CTO, Vault Financial", quote: "Our customer portal was a nightmare of duct tape. Their team designed and shipped a beautiful, secure replacement.", avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop" },
  { name: "Priya Malhotra", role: "Head of Growth, Cadence", quote: "The blog content they produce ranks. Not eventually — quickly. And it actually sounds like humans wrote it.", avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop" },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-brand-border">
        <div className="absolute inset-0 grid-lines pointer-events-none opacity-60" />
        <div className="container-x pt-16 pb-16 lg:pt-24 lg:pb-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-surface border border-brand-border text-xs font-medium text-brand-muted mb-8" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Accepting new projects for Q1
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-brand-ink" data-testid="hero-heading">
                We design, build, and scale the <span className="text-brand-blue">digital arm</span> of ambitious companies.
              </h1>
              <p className="mt-8 text-lg sm:text-xl text-brand-muted max-w-2xl leading-relaxed">
                TelstraNics is a full-service digital studio — from strategy and design to development, marketing, and long-term maintenance. One team, end-to-end.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">Start a Project <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/pricing" data-testid="hero-cta-secondary" className="btn-outline">See Pricing</Link>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div className="relative aspect-[4/5] lg:aspect-[5/6] w-full overflow-hidden rounded-3xl bg-brand-surface">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1400&q=80&auto=format&fit=crop"
                  alt="TelstraNics team collaborating on digital projects"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
              </div>
              <div className="hidden lg:flex absolute -bottom-6 -left-6 bg-white border border-brand-border rounded-2xl p-4 shadow-xl items-center gap-3 max-w-[240px]">
                <div className="w-10 h-10 rounded-full bg-brand-blue/10 text-brand-blue flex items-center justify-center font-display font-bold">✦</div>
                <div>
                  <div className="text-sm font-medium text-brand-ink leading-tight">Senior-only team</div>
                  <div className="text-xs text-brand-muted mt-0.5">Direct access. No middlemen.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-24 lg:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">Our Services</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Everything digital, under one roof.</h2>
          </div>
          <Link to="/pricing" data-testid="home-services-cta" className="text-sm font-medium text-brand-ink inline-flex items-center gap-1 hover:gap-2 transition-all">See pricing for each <ArrowRight className="w-4 h-4" /></Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => {
            const palette = {
              "digital-marketing":    { bg: "bg-orange-100",  ring: "ring-orange-200",  text: "text-orange-600" },
              "website-design":       { bg: "bg-pink-100",    ring: "ring-pink-200",    text: "text-pink-600" },
              "website-development":  { bg: "bg-blue-100",    ring: "ring-blue-200",    text: "text-blue-600" },
              "website-maintenance":  { bg: "bg-emerald-100", ring: "ring-emerald-200", text: "text-emerald-600" },
              "ecommerce-websites":   { bg: "bg-violet-100",  ring: "ring-violet-200",  text: "text-violet-600" },
              "web-portals":          { bg: "bg-cyan-100",    ring: "ring-cyan-200",    text: "text-cyan-600" },
              "logo-design":          { bg: "bg-amber-100",   ring: "ring-amber-200",   text: "text-amber-600" },
              "content-writing":      { bg: "bg-rose-100",    ring: "ring-rose-200",    text: "text-rose-600" },
            }[s.slug] || { bg: "bg-brand-surface", ring: "ring-brand-border", text: "text-brand-ink" };
            return (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`home-service-card-${s.slug}`}
                className="group relative bg-white border border-brand-border rounded-2xl p-7 card-hover overflow-hidden">
                <div className={`w-20 h-20 rounded-2xl ${palette.bg} ring-1 ${palette.ring} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <s.icon className={`w-10 h-10 ${palette.text}`} strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-bold text-lg text-brand-ink mb-1.5">{s.title}</h3>
                <p className="text-sm text-brand-muted leading-relaxed line-clamp-2">{s.summary}</p>
                <ArrowUpRight className="absolute top-6 right-6 w-4 h-4 text-brand-muted group-hover:text-brand-ink transition-colors" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-brand-ink text-white">
        <div className="container-x py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-blue mb-3">Why TelstraNics</div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl text-white tracking-tight">Senior team. Direct access. Real outcomes.</h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                No middlemen. No juniors doing your work. You get a small pod of seniors who own the outcome, from strategy through launch and beyond.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Zap, title: "Senior-only team", body: "Every project is led by 8+ year veterans across design, engineering, and marketing." },
                { icon: Shield, title: "Fixed-price options", body: "Predictable pricing on standard packages. No surprise invoices, ever." },
                { icon: Trophy, title: "Outcome-focused", body: "We measure success by your metrics: pipeline, revenue, retention — not deliverables." },
                { icon: Star, title: "Long-term partners", body: "70% of clients renew or expand. We're built for the marathon, not the sprint." },
              ].map((c) => (
                <div key={c.title} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                  <c.icon className="w-5 h-5 text-brand-blue mb-4" />
                  <div className="font-display font-bold text-white mb-1.5">{c.title}</div>
                  <div className="text-sm text-white/60 leading-relaxed">{c.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-x py-24 lg:py-32">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-3">Kind Words</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">Real teams. Real results.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => (
            <figure key={t.name} data-testid={`testimonial-${t.name.split(' ')[0].toLowerCase()}`} className="bg-white border border-brand-border rounded-2xl p-8">
              <div className="flex gap-0.5 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-brand-amber text-brand-amber" />)}</div>
              <blockquote className="text-brand-ink text-lg leading-relaxed">"{t.quote}"</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="font-medium text-brand-ink text-sm">{t.name}</div>
                  <div className="text-xs text-brand-muted">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-24 lg:pb-32">
        <div className="relative bg-brand-surface border border-brand-border rounded-3xl p-12 lg:p-20 overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-70" />
          <div className="relative max-w-3xl">
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-ink">Have a project in mind?</h2>
            <p className="mt-6 text-lg text-brand-muted max-w-xl">Tell us about it. We'll respond within one business day with next steps — and if we're not the right fit, we'll say so.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="home-final-cta" className="btn-primary">Start a Project <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/pricing" data-testid="home-final-cta-secondary" className="btn-outline">View Pricing</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
